/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { handleUnknownCaughtError } from '@karya/error-values';
import { useState, useEffect, useCallback } from 'react';

export type AsyncData<T, E extends Error> = {
  triggerFetch: () => void;
} & ( // Loading state
  | {
      data: null;
      isLoading: true;
      error: null;
    }
  // Success state
  | {
      data: T;
      isLoading: false;
      error: null;
    }
  // Error state
  | {
      data: null;
      isLoading: false;
      error: E;
    }
);

/**
 * @template T  Type of the awaited data
 * @param asyncFunction The asynchronous function that fetches the data
 * @param options Optional settings:
 *   - skip: If true, the asyncFunction will not be called
 *   - deps: Dependency array to control when to re-run the asyncFunction.
 *           If not provided, the function is only run once on mount.
 * @returns
 */
function useAsyncData<T>(
  asyncFunction: () => Promise<T>,
  options?: { skip?: boolean; deps: unknown[] },
): AsyncData<T, Error> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cachedAsyncFunction = useCallback(asyncFunction, options?.deps ?? []);

  const triggerFetch = useCallback(() => {
    // Variable to keep track of if the retrigger is still mounted
    // to avoid setting state on unmounted component
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await cachedAsyncFunction();
        if (isMounted) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        const errorObj = err instanceof Error ? err : handleUnknownCaughtError(err);
        if (isMounted) {
          setError(errorObj);
          setData(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    void fetchData();

    return () => {
      isMounted = false;
    };
  }, [cachedAsyncFunction]);

  useEffect(() => {
    if (options?.skip) {
      setLoading(false);
      return;
    }

    const abort = triggerFetch();
    return abort;
  }, [options?.skip, triggerFetch]);

  return { data, isLoading, error, triggerFetch } as AsyncData<T, Error>;
}

export default useAsyncData;
